import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { InvoiceAction, Sale, SaleItem } from '@/lib/types';

export interface InvoiceSearchFilters {
  invoiceNumber: string;
  customerName: string;
  date: string;
  cashierId: string | null;
  branchId: string | null;
}

export interface RefundLine {
  saleItemId: string;
  quantity: number;
}

function dayRange(date: string): { startISO: string; endISO: string } {
  const start = new Date(`${date}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

export function useInvoiceAdmin() {
  const [results, setResults] = useState<Sale[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (filters: InvoiceSearchFilters) => {
    setSearching(true);
    setError(null);
    try {
      let query = supabase
        .from('sales')
        .select('*, branch:branches(name)')
        .order('created_at', { ascending: false })
        .limit(50);

      if (filters.invoiceNumber.trim()) {
        query = query.ilike('invoice_number', `%${filters.invoiceNumber.trim()}%`);
      }
      if (filters.customerName.trim()) {
        query = query.ilike('customer_name', `%${filters.customerName.trim()}%`);
      }
      if (filters.date) {
        const { startISO, endISO } = dayRange(filters.date);
        query = query.gte('created_at', startISO).lt('created_at', endISO);
      }
      if (filters.cashierId) query = query.eq('cashier_id', filters.cashierId);
      if (filters.branchId) query = query.eq('branch_id', filters.branchId);

      const { data, error: e } = await query;
      if (e) throw e;
      setResults((data as Sale[]) ?? []);
      setSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron buscar las facturas');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const clear = useCallback(() => {
    setResults([]);
    setSearched(false);
    setError(null);
  }, []);

  return { results, searching, error, searched, search, clear, setError };
}

export async function loadInvoiceDetail(saleId: string): Promise<{
  sale: Sale | null;
  items: SaleItem[];
  actions: InvoiceAction[];
  error: string | null;
}> {
  try {
    const [{ data: saleData, error: saleErr }, { data: itemData, error: itemErr }, { data: actionData }] =
      await Promise.all([
        supabase
          .from('sales')
          .select('*, branch:branches(name)')
          .eq('id', saleId)
          .maybeSingle(),
        supabase.from('sale_items').select('*').eq('sale_id', saleId).order('created_at', { ascending: true }),
        supabase
          .from('invoice_actions')
          .select('*')
          .eq('sale_id', saleId)
          .order('created_at', { ascending: false }),
      ]);
    if (saleErr) throw saleErr;
    if (itemErr) throw itemErr;
    return {
      sale: (saleData as Sale) ?? null,
      items: (itemData as SaleItem[]) ?? [],
      actions: (actionData as InvoiceAction[]) ?? [],
      error: null,
    };
  } catch (err) {
    return {
      sale: null,
      items: [],
      actions: [],
      error: err instanceof Error ? err.message : 'No se pudo cargar la factura',
    };
  }
}

export async function refundInvoice(
  saleId: string,
  lines: RefundLine[],
  reason: string,
  full: boolean
): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('refund_sale', {
    payload: {
      sale_id: saleId,
      full,
      reason,
      items: lines.map((l) => ({ sale_item_id: l.saleItemId, quantity: l.quantity })),
    },
  });
  return { error: error?.message ?? null };
}

export async function voidInvoice(
  saleId: string,
  reason: string,
  action: 'void' | 'delete'
): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('void_sale', {
    payload: { sale_id: saleId, reason, action },
  });
  return { error: error?.message ?? null };
}